import React, { useState } from "react";
import { Download, X } from "lucide-react";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];

const normalizeSearch = text =>
  String(text ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("it-IT")
    .trim();

const esc = value => String(value ?? "")
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;")
  .replace(/'/g, "&#39;");

const value = v => v === null || v === undefined || v === "" ? "—" : esc(v);

const period = x =>
  `${value(x.start_date)} – ${value(x.end_date)}: ${value(x.reason)}`;

function facilityCoordinatesHtml(f) {
  if (f.latitude === null || f.latitude === undefined ||
      f.longitude === null || f.longitude === undefined ||
      f.latitude === "" || f.longitude === "") {
    return "";
  }

  const lat = Number(f.latitude);
  const lon = Number(f.longitude);

  if (!Number.isFinite(lat) || !Number.isFinite(lon) ||
      lat < -90 || lat > 90 || lon < -180 || lon > 180) {
    return "";
  }

  const latitude = lat.toFixed(6);
  const longitude = lon.toFixed(6);

  const mapUrl =
    `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}` +
    `#map=16/${latitude}/${longitude}`;

  return `<div class="coordinates">
    <strong>Coordinate GPS:</strong> ${latitude}, ${longitude}
    · <a href="${esc(mapUrl)}" target="_blank" rel="noopener noreferrer">Apri mappa</a>
  </div>`;
}

function serviceHtml(s) {
  let availability;

  if (s.h24) {
    availability = "H24 · tutti i giorni";
  } else {
    const schedule = Array.isArray(s.schedule) && s.schedule.length
      ? s.schedule
      : [{ days: s.days || [], start: s.start, end: s.end }];

    availability = schedule.map(slot => {
      const days = (slot.days || [])
        .map(d => DAYS[Number(d)] || String(d))
        .join(", ");

      return `<div>${value(days)} · ${value(slot.start)}–${value(slot.end)}</div>`;
    }).join("");
  }

  const exceptions = (s.exceptions || []).length
    ? `<div class="exceptions"><strong>Eccezioni:</strong>
       <ul>${s.exceptions.map(x => `<li>${period(x)}</li>`).join("")}</ul></div>`
    : "";

  return `
    <div class="service">
      <div class="service-title">
        <strong>${value(s.service_name)}</strong>
        <span>${s.active === false ? "Sospeso" : "Attivo"}</span>
      </div>
      <div>Categoria: ${value(s.service_category)}</div>
      <div>Disponibilità: ${availability}</div>
      <div>Note: ${value(s.notes)}</div>
      ${exceptions}
    </div>`;
}

function buildHtml(items, filters) {
  const date = new Date().toLocaleString("it-IT");

  const cards = items.map((f, index) => `
    <section class="facility">
      <h2>${index + 1}. ${value(f.name)}</h2>
      <div class="details">
        <div><strong>Tipologia:</strong> ${value(f.type)}</div>
        <div><strong>Stato:</strong> ${f.active === false ? "Disattiva" : "Attiva"}</div>
        <div><strong>Indirizzo:</strong> ${value(f.address)}</div>
        <div><strong>Comune:</strong> ${value(f.city)}</div>
        <div><strong>Provincia:</strong> ${value(f.province)}</div>
        <div><strong>Telefono:</strong> ${value(f.phone)}</div>
        ${facilityCoordinatesHtml(f)}
      </div>
      <div class="notes"><strong>Note struttura:</strong> ${value(f.notes)}</div>
      <h3>Servizi associati (${(f.services || []).length})</h3>
      ${(f.services || []).length
        ? f.services.map(serviceHtml).join("")
        : '<p>Nessun servizio associato.</p>'}
    </section>
  `).join("");

  return `<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="utf-8">
<title>118Finder - Esportazione strutture</title>
<style>
  @page { size: A4; margin: 15mm; }
  body { font-family: Arial, sans-serif; color: #182635; font-size: 10pt; line-height: 1.45; }
  h1 { font-size: 20pt; margin-bottom: 4mm; color: #123b59; }
  h2 { font-size: 14pt; color: #123b59; margin: 0 0 5mm; }
  h3 { font-size: 11pt; margin: 6mm 0 3mm; }
  .summary { background: #eef3f7; padding: 4mm; margin-bottom: 8mm; }
  .facility { border-top: 2px solid #315c78; margin-top: 8mm; padding-top: 5mm; }
  .details { display: grid; grid-template-columns: 1fr 1fr; gap: 2mm 6mm; }
  .notes { margin-top: 4mm; white-space: pre-wrap; }
  .service { border: 1px solid #ccd7e0; padding: 3mm; margin: 3mm 0; break-inside: avoid; page-break-inside: avoid; }
  .service-title { display: flex; justify-content: space-between; gap: 5mm; margin-bottom: 2mm; color: #123b59; }
  .exceptions { margin-top: 2mm; }
  .exceptions ul { margin: 1mm 0; padding-left: 5mm; }
  .print-button { padding: 10px 16px; margin: 12px 0; cursor: pointer; }
  @media print {
    .print-button { display: none; }
    .summary { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style>
</head>
<body>
<button class="print-button" onclick="window.print()">Stampa / Salva come PDF</button>
<h1>118Finder · Anagrafica strutture</h1>
<div class="summary">
  <div><strong>Data esportazione:</strong> ${esc(date)}</div>
  <div><strong>Strutture esportate:</strong> ${items.length}</div>
  <div><strong>Provincia:</strong> ${value(filters.province || "Tutte")}</div>
  <div><strong>Tipologia:</strong> ${value(filters.type || "Tutte")}</div>
  <div><strong>Ricerca:</strong> ${value(filters.q || "Nessuna")}</div>
  <div><strong>Stato:</strong> ${value(filters.active || "Tutti")}</div>
  <div><strong>Servizio associato:</strong> ${value(filters.service || "Tutti")}</div>
</div>
${cards}
</body>
</html>`;
}

export default function FacilitiesPdfExport({ facilities, facilityTypes, api }) {
  const [open, setOpen] = useState(false);
  const [filters, setFilters] = useState({
    q: "", type: "", province: "", active: "", service: ""
  });
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [serviceData, setServiceData] = useState(null);
  const [serviceLoading, setServiceLoading] = useState(false);
  const [serviceError, setServiceError] = useState("");

  async function openDialog() {
    setOpen(true);
    setError("");
    setServiceError("");
    setServiceLoading(true);
    setServiceData(null);

    try {
      const response = await api.get(
        "/admin/export/facilities/pdf-data",
        { timeout: 120000 }
      );
      if (!Array.isArray(response.data?.items)) {
        throw new Error("Formato dati non valido.");
      }
      setServiceData(response.data.items);
    } catch (e) {
      setServiceError(
        e.response?.data?.detail ||
        e.message ||
        "Impossibile caricare i servizi associati."
      );
    } finally {
      setServiceLoading(false);
    }
  }

  const servicesByFacility = new Map(
    (serviceData || []).map(f => [f.id, f.services || []])
  );

  const provinces = [...new Set(facilities.map(f => f.province).filter(Boolean))].sort();
  const types = [...new Set([
    ...facilityTypes.map(t => t.name),
    ...facilities.map(f => f.type)
  ].filter(Boolean))].sort();

  const candidates = facilities.filter(f => {
    if (filters.service) {
      const query = normalizeSearch(filters.service);
      const associated = servicesByFacility.get(f.id) || [];
      if (!associated.some(s =>
        normalizeSearch(s.service_name).includes(query)
      )) return false;
    }
    if (filters.q && !`${f.name} ${f.address || ""} ${f.city || ""}`
      .toLowerCase().includes(filters.q.toLowerCase())) return false;
    if (filters.type && f.type !== filters.type) return false;
    if (filters.province && f.province !== filters.province) return false;
    if (filters.active === "active" && f.active === false) return false;
    if (filters.active === "inactive" && f.active !== false) return false;
    return true;
  }).sort((a, b) => a.name.localeCompare(b.name, "it"));

  const selectedItems = candidates.filter(f => selected === null || selected.includes(f.id));
  const setField = (field, v) => setFilters(prev => ({ ...prev, [field]: v }));

  const toggle = id => {
    const current = selected === null ? candidates.map(f => f.id) : selected;
    setSelected(current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id]);
  };

  async function exportPdf() {
    if (!selectedItems.length || loading) return;

    setLoading(true);
    setError("");

    // Aperta direttamente dal clic, evitando il blocco popup del browser.
    const popup = window.open("", "_blank");

    if (!popup) {
      setError("Il browser ha bloccato la finestra di stampa. Abilita i popup per questo sito.");
      setLoading(false);
      return;
    }

    popup.document.write("<p>Preparazione documento 118Finder...</p>");
    popup.document.close();

    try {
      const ids = new Set(selectedItems.map(f => f.id));
      const response = await api.get("/admin/export/facilities/pdf-data", {
        timeout: 120000
      });

      if (!Array.isArray(response.data?.items)) {
        throw new Error("Formato dati non valido.");
      }

      const items = response.data.items.filter(f => {
        if (!ids.has(f.id)) return false;
        if (!filters.service.trim()) return true;
        return (f.services || []).some(s =>
          normalizeSearch(s.service_name)
            .includes(normalizeSearch(filters.service))
        );
      });

      if (items.length !== selectedItems.length) {
        throw new Error("Alcune strutture selezionate non sono presenti nell'esportazione.");
      }

      popup.document.open();
      popup.document.write(buildHtml(items, filters));
      popup.document.close();
      popup.focus();
    } catch (e) {
      popup.close();
      setError(e.response?.data?.detail || e.message || "Esportazione PDF non riuscita.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <button type="button" className="row-btn"
        data-testid="export-facilities-pdf-button"
        onClick={openDialog}>
        <Download size={13}/> Esporta PDF
      </button>

      {open && (
        <div className="service-mgr-overlay"
          onClick={e => { if (e.target === e.currentTarget && !loading) setOpen(false); }}>
          <div className="service-mgr-panel" style={{ maxWidth: 850, width: "95%" }}>
            <div className="service-mgr-head">
              <span className="eyebrow">ESPORTAZIONE PDF</span>
              <b>Seleziona le strutture da esportare</b>
              <button type="button" className="icon-btn"
                disabled={loading} onClick={() => setOpen(false)}>
                <X size={22}/>
              </button>
            </div>

            <div className="user-create-form">
              <div className="user-create-grid">
                <label className="span-2">
                  Cerca struttura, indirizzo o comune
                  <input value={filters.q} onChange={e => setField("q", e.target.value)}
                    placeholder="Cerca..."/>
                </label>
                <label>Provincia
                  <select value={filters.province} onChange={e => setField("province", e.target.value)}>
                    <option value="">Tutte le province</option>
                    {provinces.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </label>
                <label>Tipologia
                  <select value={filters.type} onChange={e => setField("type", e.target.value)}>
                    <option value="">Tutte le tipologie</option>
                    {types.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </label>
                <label className="span-2">
                  Cerca servizio associato
                  <input
                    value={filters.service}
                    onChange={e => setField("service", e.target.value)}
                    placeholder="Es. Guardia medica"
                  />
                </label>
                <label>Stato struttura
                  <select value={filters.active} onChange={e => setField("active", e.target.value)}>
                    <option value="">Tutte</option>
                    <option value="active">Solo attive</option>
                    <option value="inactive">Solo disattive</option>
                  </select>
                </label>
              </div>

              {serviceLoading && (
                <p>Caricamento dei servizi associati...</p>
              )}
              {serviceError && (
                <div className="error" style={{ marginTop: 12 }}>
                  {serviceError}
                </div>
              )}
              <div className="service-mgr-toolbar" style={{ marginTop: 16 }}>
                <button type="button" className="row-btn"
                  onClick={() => setSelected(null)}>Seleziona tutte</button>
                <button type="button" className="row-btn"
                  onClick={() => setSelected([])}>Deseleziona tutte</button>
                <span>{selectedItems.length} di {candidates.length} strutture selezionate</span>
              </div>

              <div style={{
                maxHeight: 300, overflowY: "auto",
                border: "1px solid #ccd7e0", padding: 12, borderRadius: 6
              }}>
                {candidates.length === 0 && !serviceLoading && !serviceError &&
                  <p>Nessuna struttura corrisponde ai filtri.</p>}
                {candidates.map(f => (
                  <label key={f.id} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "7px 0", cursor: "pointer"
                  }}>
                    <input type="checkbox"
                      checked={selected === null || selected.includes(f.id)}
                      onChange={() => toggle(f.id)}/>
                    <span>{f.name} · {f.city || "—"} ({f.province || "—"})</span>
                  </label>
                ))}
              </div>

              {error && <div className="error" style={{ marginTop: 12 }}>{error}</div>}

              <div className="user-create-actions">
                <button type="button" disabled={loading}
                  onClick={() => setOpen(false)}>Annulla</button>
                <button type="button" className="primary-btn"
                  disabled={
                     loading ||
                     !selectedItems.length ||
                     (Boolean(filters.service.trim()) &&
                       (!serviceData || serviceLoading))
                   }
                  onClick={exportPdf}>
                  <Download size={15}/>
                  {loading ? "Preparazione..." : `Prepara PDF (${selectedItems.length})`}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
